import React from 'react';
import SearchAndPagination from './SearchAndPagination';

/**
 * EngagementsTab
 *
 * Props:
 * - sessions: array
 * - handleViewResults: (session) => void
 * - getStatusBadge: (status) => ReactNode
 * - formatTime: (timestamp) => string
 */
const EngagementsTab = ({ sessions, handleViewResults, getStatusBadge, formatTime }) => {
  // Filter for completed sessions that:
  // 1. Are consultant-led (from live_sessions with consultant_id)
  // 2. Have a valid assessment_id (assessment exists in completed_assessments)
  // 3. Are from live_session source (not self-guided)
  const completedSessions = sessions.filter((session) => 
    session.status === 'completed' && 
    session.assessmentId && // Must have a valid assessment_id (assessment exists)
    (session.isConsultantLed === true || session.source === 'live_session')
  );

  const renderSessions = (sessionsToRender) => {
    if (!sessionsToRender.length) {
      return (
        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
          <svg
            className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p>No completed engagements found</p>
        </div>
      );
    }

    return sessionsToRender.map((session) => (
    <div key={session.id} className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white truncate">
              {session.clientName}
            </h3>
            <div className="flex-shrink-0">
              {getStatusBadge(session.status)}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="min-w-0">
              <span className="font-medium">Industry:</span>{' '}
            
              <span className="truncate block sm:inline">{session.industry || 'Not specified'}</span>
            </div>
            <div className="min-w-0">
              <span className="font-medium">Email:</span>{' '}
              <span className="truncate block sm:inline">{session.clientEmail || 'N/A'}</span>
            </div>
            <div className="min-w-0">
              <span className="font-medium">Completed:</span>{' '}
              <span className="whitespace-nowrap">
                {session.completedTime && !isNaN(new Date(session.completedTime).getTime())
                  ? formatTime(session.completedTime)
                  : 'N/A'}
              </span>
            </div>
          </div>
          {session.totalSavings > 0 && (
            <div className="mt-3 text-sm">
              <span className="font-medium text-emerald-600 dark:text-emerald-400">
                Potential Savings: ${session.totalSavings.toLocaleString()}/year
              </span>
            </div>
          )}
        </div>
        <div className="flex sm:flex-col sm:ml-6 sm:flex-shrink-0">
          <button
            onClick={() => handleViewResults(session)}
            className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            disabled={!session.assessmentId}
          >
            View Results
          </button>
        </div>
      </div>
    </div>
    ));
  };

  if (!completedSessions.length) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        <svg
          className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
        <p>No completed engagements</p>
      </div>
    );
  }

  return (
    <SearchAndPagination
      data={completedSessions}
      searchFields={['clientName', 'clientEmail', 'industry']}
      itemsPerPage={10}
      searchPlaceholder="Search engagements by name, email, or industry..."
      renderItems={renderSessions}
    />
  );
};

export default EngagementsTab;


